import { useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { ScrollArea } from "./ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  MoreVertical,
  Reply,
  Forward,
  Copy,
  Edit3,
  Info,
  Pin,
  Languages,
  Star,
  StarOff,
  Clock,
  Loader2,
  Check,
  X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Message Action Menu Component
export const MessageActionMenu = ({ 
  message, 
  isOwn, 
  onReply, 
  onForward, 
  onEdit, 
  onPin, 
  onHighlight,
  onTranslate,
  onInfo,
  conversations = []
}) => {
  const [showForwardDialog, setShowForwardDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [showTranslateDialog, setShowTranslateDialog] = useState(false);
  const [forwardTarget, setForwardTarget] = useState("");
  const [editContent, setEditContent] = useState(message.content || "");
  const [messageInfo, setMessageInfo] = useState(null);
  const [translatedText, setTranslatedText] = useState("");
  const [targetLang, setTargetLang] = useState("en");
  const [loading, setLoading] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      toast.success("Mensaje copiado");
    } catch (err) {
      toast.error("Error al copiar");
    }
  };

  const handleForward = async () => {
    if (!forwardTarget) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/messages/${message.message_id}/forward`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({ 
          message_id: message.message_id,
          target_conversation_id: forwardTarget 
        })
      });
      if (res.ok) {
        toast.success("Mensaje reenviado");
        setShowForwardDialog(false);
      } else {
        toast.error("Error al reenviar");
      }
    } catch (err) {
      toast.error("Error al reenviar");
    }
    setLoading(false);
  };

  const handleEdit = async () => {
    if (!editContent.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/messages/${message.message_id}/edit`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({ content: editContent })
      });
      if (res.ok) {
        toast.success("Mensaje editado");
        setShowEditDialog(false);
        if (onEdit) onEdit(message.message_id, editContent);
      } else {
        const data = await res.json();
        toast.error(data.detail || "Error al editar");
      }
    } catch (err) {
      toast.error("Error al editar");
    }
    setLoading(false);
  };

  const handlePin = async () => {
    try {
      const isPinned = message.is_pinned;
      const method = isPinned ? "DELETE" : "POST";
      const res = await fetch(`${API}/api/messages/${message.message_id}/pin`, {
        method,
        headers: { ...getAuthHeaders() },
        credentials: "include"
      });
      if (res.ok) {
        toast.success(isPinned ? "Mensaje desfijado" : "Mensaje fijado");
        if (onPin) onPin(message.message_id, !isPinned);
      }
    } catch (err) {
      toast.error("Error al fijar mensaje");
    }
  };

  const handleHighlight = async () => {
    try {
      const isHighlighted = message.is_highlighted;
      const method = isHighlighted ? "DELETE" : "POST";
      const res = await fetch(`${API}/api/messages/${message.message_id}/highlight`, {
        method,
        headers: { ...getAuthHeaders() },
        credentials: "include"
      });
      if (res.ok) {
        toast.success(isHighlighted ? "Destacado removido" : "Mensaje destacado");
        if (onHighlight) onHighlight(message.message_id, !isHighlighted);
      }
    } catch (err) {
      toast.error("Error");
    }
  };

  const handleGetInfo = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/messages/${message.message_id}/info`, {
        headers: { ...getAuthHeaders() },
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setMessageInfo(data);
        setShowInfoDialog(true);
      }
    } catch (err) {
      toast.error("Error al obtener información");
    }
    setLoading(false);
  };

  const handleTranslate = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/translate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({ text: message.content, target_language: targetLang })
      });
      if (res.ok) {
        const data = await res.json();
        setTranslatedText(data.translated_text);
      } else {
        toast.error("Error al traducir");
      }
    } catch (err) {
      toast.error("Error al traducir");
    }
    setLoading(false);
  };

  const languages = [
    { code: "es", name: "Español" },
    { code: "en", name: "Inglés" },
    { code: "pt", name: "Portugués" },
    { code: "fr", name: "Francés" },
    { code: "de", name: "Alemán" },
    { code: "it", name: "Italiano" },
    { code: "zh", name: "Chino" },
    { code: "ja", name: "Japonés" },
    { code: "ko", name: "Coreano" },
    { code: "ru", name: "Ruso" },
    { code: "ar", name: "Árabe" },
  ];

  if (message.is_deleted) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="p-1.5 rounded-full bg-[#232e3c] shadow-md hover:bg-[#2b3a4c] transition-colors">
            <MoreVertical className="w-4 h-4 text-slate-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-[#232e3c] border-[#0e1621] min-w-[180px]">
          <DropdownMenuItem onClick={() => onReply?.(message)} className="text-white hover:bg-[#2b5278] cursor-pointer">
            <Reply className="w-4 h-4 mr-2" /> Responder
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowForwardDialog(true)} className="text-white hover:bg-[#2b5278] cursor-pointer">
            <Forward className="w-4 h-4 mr-2" /> Reenviar
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleCopy} className="text-white hover:bg-[#2b5278] cursor-pointer">
            <Copy className="w-4 h-4 mr-2" /> Copiar
          </DropdownMenuItem>
          {isOwn && message.message_type === "text" && (
            <DropdownMenuItem onClick={() => setShowEditDialog(true)} className="text-white hover:bg-[#2b5278] cursor-pointer">
              <Edit3 className="w-4 h-4 mr-2" /> Editar
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator className="bg-slate-700" />
          <DropdownMenuItem onClick={handleGetInfo} className="text-white hover:bg-[#2b5278] cursor-pointer">
            <Info className="w-4 h-4 mr-2" /> Info
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handlePin} className="text-white hover:bg-[#2b5278] cursor-pointer">
            <Pin className="w-4 h-4 mr-2" /> {message.is_pinned ? "Desfijar" : "Fijar"}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowTranslateDialog(true)} className="text-white hover:bg-[#2b5278] cursor-pointer">
            <Languages className="w-4 h-4 mr-2" /> Traducir
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleHighlight} className="text-white hover:bg-[#2b5278] cursor-pointer">
            {message.is_highlighted ? (
              <><StarOff className="w-4 h-4 mr-2" /> Quitar destacado</>
            ) : (
              <><Star className="w-4 h-4 mr-2" /> Destacar</>
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Forward Dialog */}
      <Dialog open={showForwardDialog} onOpenChange={setShowForwardDialog}>
        <DialogContent className="bg-[#17212b] border-[#0e1621] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Reenviar mensaje</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={forwardTarget} onValueChange={setForwardTarget}>
              <SelectTrigger className="bg-[#242f3d] border-transparent text-white">
                <SelectValue placeholder="Seleccionar conversación" />
              </SelectTrigger>
              <SelectContent className="bg-[#242f3d] border-[#0e1621]">
                {conversations.map(conv => (
                  <SelectItem key={conv.conversation_id} value={conv.conversation_id} className="text-white hover:bg-[#2b5278]">
                    {conv.other_user?.name || conv.name || "Chat"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="bg-[#242f3d] p-3 rounded-lg text-slate-300 text-sm">
              {message.content?.substring(0, 200)}{message.content?.length > 200 ? "..." : ""}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForwardDialog(false)} className="text-white">
              Cancelar
            </Button>
            <Button onClick={handleForward} disabled={!forwardTarget || loading} className="bg-red-600 hover:bg-red-700">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reenviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-[#17212b] border-[#0e1621] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Editar mensaje</DialogTitle>
          </DialogHeader>
          <Textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="bg-[#242f3d] border-transparent text-white min-h-[100px]"
          />
          <p className="text-xs text-slate-500">Solo puedes editar mensajes dentro de las primeras 24 horas</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditDialog(false)} className="text-white">
              Cancelar
            </Button>
            <Button onClick={handleEdit} disabled={!editContent.trim() || loading} className="bg-red-600 hover:bg-red-700">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Info Dialog */}
      <Dialog open={showInfoDialog} onOpenChange={setShowInfoDialog}>
        <DialogContent className="bg-[#17212b] border-[#0e1621] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Información del mensaje</DialogTitle>
          </DialogHeader>
          {messageInfo && (
            <div className="space-y-4">
              <div className="bg-[#242f3d] p-3 rounded-lg">
                <p className="text-slate-300 text-sm">{messageInfo.content?.substring(0, 300)}</p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Enviado:</span>
                  <span className="text-white">{new Date(messageInfo.created_at).toLocaleString('es-MX')}</span>
                </div>
                {messageInfo.edited_at && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Editado:</span>
                    <span className="text-white">{new Date(messageInfo.edited_at).toLocaleString('es-MX')}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Fijado:</span>
                  <span className="text-white">{messageInfo.is_pinned ? "Sí" : "No"}</span>
                </div>
              </div>
              {messageInfo.read_by?.length > 0 && (
                <div>
                  <p className="text-slate-500 text-sm mb-2">Leído por:</p>
                  <div className="space-y-2">
                    {messageInfo.read_by.map(user => (
                      <div key={user.user_id} className="flex items-center space-x-2">
                        <Avatar className="w-6 h-6">
                          <AvatarImage src={user.picture} />
                          <AvatarFallback className="bg-red-600 text-white text-xs">{user.name?.[0]}</AvatarFallback>
                        </Avatar>
                        <span className="text-white text-sm">{user.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Translate Dialog */}
      <Dialog open={showTranslateDialog} onOpenChange={setShowTranslateDialog}>
        <DialogContent className="bg-[#17212b] border-[#0e1621] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Traducir mensaje</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#242f3d] p-3 rounded-lg">
              <p className="text-slate-400 text-xs mb-1">Original:</p>
              <p className="text-white text-sm">{message.content}</p>
            </div>
            <Select value={targetLang} onValueChange={setTargetLang}>
              <SelectTrigger className="bg-[#242f3d] border-transparent text-white">
                <SelectValue placeholder="Idioma destino" />
              </SelectTrigger>
              <SelectContent className="bg-[#242f3d] border-[#0e1621]">
                {languages.map(lang => (
                  <SelectItem key={lang.code} value={lang.code} className="text-white hover:bg-[#2b5278]">
                    {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {translatedText && (
              <div className="bg-[#1e3a5f] p-3 rounded-lg">
                <p className="text-slate-400 text-xs mb-1">Traducción:</p>
                <p className="text-white text-sm">{translatedText}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTranslateDialog(false)} className="text-white">
              Cerrar
            </Button>
            <Button onClick={handleTranslate} disabled={loading} className="bg-red-600 hover:bg-red-700">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Traducir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Temporary Message Timer Display
export const TemporaryMessageTimer = ({ expiresAt }) => {
  const [timeLeft, setTimeLeft] = useState("");

  useState(() => {
    const updateTimer = () => {
      const now = new Date();
      const expires = new Date(expiresAt);
      const diff = expires - now;
      
      if (diff <= 0) {
        setTimeLeft("Expirado");
        return;
      }
      
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  return (
    <span className="flex items-center text-orange-400 text-xs">
      <Clock className="w-3 h-3 mr-1" />
      {timeLeft}
    </span>
  );
};

export default MessageActionMenu;
